import React, { forwardRef } from 'react';
import cx from 'classnames';

export interface CloseButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** Read by screen readers and shown as the tooltip. */
  label?: string;
  /** `sm` is for tight places only: tooltips and notification strips. */
  size?: 'sm' | 'md';
  /** `inverse` goes on dark surfaces; `solid` floats over content of unknown colour (a tool canvas, an image). */
  tone?: 'default' | 'inverse' | 'solid';
}

const sizeClasses = { sm: 'size-6', md: 'size-8' };
const iconSize = { sm: 14, md: 20 };
const toneClasses = {
  default: 'text-ink hover:bg-surface-muted active:bg-line',
  inverse: 'text-white hover:bg-white/10 active:bg-white/20',
  solid: 'bg-white text-ink shadow-raised hover:bg-surface-muted active:bg-line',
};

/** The one close control of the app — every dialog, panel and notification uses this, never its own cross. */
export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(
  ({ label = 'Закрыть', size = 'md', tone = 'default', type = 'button', className, ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-control transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        'disabled:cursor-not-allowed disabled:opacity-50',
        sizeClasses[size],
        toneClasses[tone],
        className,
      )}
      {...rest}
    >
      <svg width={iconSize[size]} height={iconSize[size]} viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M18 18L12 12M12 12L6 6M12 12L18 6M12 12L6 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  ),
);

CloseButton.displayName = 'CloseButton';
